/**
 * Управление 2FA (TOTP) для текущего юзера.
 *  - Status (enabled/disabled)
 *  - Enroll → QR + backup codes → подтверждение первым кодом
 *  - Disable (требует код для защиты от случайного отключения)
 */
import { useEffect, useState } from 'react';
import { Loader2, Save, ShieldCheck, ShieldOff, KeyRound } from 'lucide-react';
import { api } from '../../api/client';

interface EnrollResponse {
  provisioning_uri: string;
  qr_png_b64: string;
  backup_codes: string[];
}

export default function TwoFactorEditor() {
  const [status, setStatus] = useState<'loading' | 'enabled' | 'disabled'>('loading');
  const [enrollData, setEnrollData] = useState<EnrollResponse | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = () => {
    api.get<{ enabled: boolean }>('/api/v1/auth/2fa/status')
      .then(r => setStatus(r.data.enabled ? 'enabled' : 'disabled'))
      .catch(() => setStatus('disabled'));
  };
  useEffect(load, []);

  const startEnroll = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post<EnrollResponse>('/api/v1/auth/2fa/enroll');
      setEnrollData(r.data); setCode('');
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'ошибка' });
    } finally { setBusy(false); }
  };

  const confirmEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      await api.post('/api/v1/auth/2fa/enroll/verify', { code });
      setMsg({ ok: true, text: '2FA включён. Сохраните backup-коды!' });
      setEnrollData(null); load();
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'неверный код' });
    } finally { setBusy(false); }
  };

  const doDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      await api.post('/api/v1/auth/2fa/disable', { code });
      setMsg({ ok: true, text: '2FA выключен.' });
      setCode(''); load();
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'неверный код' });
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
        {status === 'enabled' ? <ShieldCheck className="text-emerald-400" size={16} />
                              : <ShieldOff className="text-slate-500" size={16} />}
        <h2 className="font-bold text-white">Двухфакторная аутентификация (TOTP)</h2>
      </header>
      <div className="p-5 max-w-md space-y-3">
        {status === 'loading' && <Loader2 className="animate-spin text-blue-400" />}

        {status === 'disabled' && !enrollData && (
          <>
            <p className="text-sm text-slate-400">
              Сейчас 2FA <b className="text-slate-200">выключен</b>. Включение требует
              приложение-аутентификатор (Google Authenticator, Authy, Aegis и т.п.).
            </p>
            <button onClick={startEnroll} disabled={busy}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded text-sm font-bold">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />}
              Включить 2FA
            </button>
          </>
        )}

        {enrollData && (
          <form onSubmit={confirmEnroll} className="space-y-3">
            <p className="text-sm text-slate-300">
              1. Отсканируйте QR в аутентификаторе либо введите ключ вручную из ссылки ниже.
            </p>
            <img src={`data:image/png;base64,${enrollData.qr_png_b64}`}
                 alt="QR" className="mx-auto bg-white p-2 rounded" style={{ width: 220 }} />
            <details className="text-[10px] text-slate-500">
              <summary className="cursor-pointer">показать otpauth URL</summary>
              <code className="break-all">{enrollData.provisioning_uri}</code>
            </details>
            <div className="bg-yellow-900/30 border border-yellow-800 text-yellow-200 px-3 py-2 rounded text-xs">
              <b>Backup-коды</b> (сохрани их — каждый одноразовый):
              <ul className="mt-1 grid grid-cols-2 gap-x-3 font-mono">
                {enrollData.backup_codes.map(c => <li key={c}>{c}</li>)}
              </ul>
            </div>
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
                2. Введите 6-значный код из аутентификатора
              </span>
              <input value={code} onChange={e => setCode(e.target.value)}
                required inputMode="numeric" autoComplete="one-time-code" autoFocus
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white font-mono" />
            </label>
            <button type="submit" disabled={busy || !code}
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white px-4 py-2 rounded text-sm font-bold">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Подтвердить и включить
            </button>
          </form>
        )}

        {status === 'enabled' && (
          <form onSubmit={doDisable} className="space-y-3">
            <p className="text-sm text-slate-300">
              2FA <b className="text-emerald-400">включён</b>. При входе будет
              запрашиваться 6-значный код или backup-код.
            </p>
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
                Для выключения введите код из аутентификатора
              </span>
              <input value={code} onChange={e => setCode(e.target.value)}
                required inputMode="numeric" autoComplete="one-time-code"
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white font-mono" />
            </label>
            <button type="submit" disabled={busy || !code}
              className="inline-flex items-center gap-1.5 bg-red-700 hover:bg-red-600 disabled:bg-slate-700 text-white px-4 py-2 rounded text-sm font-bold">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldOff size={13} />}
              Выключить 2FA
            </button>
          </form>
        )}

        {msg && (
          <div className={`px-3 py-2 rounded text-sm ${
            msg.ok ? 'bg-emerald-900/30 border border-emerald-800 text-emerald-200'
                   : 'bg-red-900/30 border border-red-800 text-red-300'
          }`}>{msg.text}</div>
        )}
      </div>
    </section>
  );
}
